import React, { useEffect, useRef, useState, useCallback } from "react";
import { X, Check, Crop as CropIcon, RotateCw, Square, RectangleVertical, Sparkles } from "lucide-react";

// Square + Story cropper with SCL watermark + optional branded story overlays.
// Aspect toggle: 1:1 (Instagram feed, 1080x1080) or 9:16 (Story/Reel, 1080x1920).
// Story-only presets add a mustard "Kindness Wall" template so posts are on-brand.
// Props: file (File), onCancel (()=>void), onConfirm ((File)=>void)

const ASPECTS = {
  square:   { id: "square", label: "Feed 1:1", icon: Square,             outW: 1080, outH: 1080, viewW: 320, viewH: 320 },
  story:    { id: "story",  label: "Story 9:16", icon: RectangleVertical, outW: 1080, outH: 1920, viewW: 200, viewH: 355 },
};
const WATERMARK = "SCL · Kindness";

// Story presets — only shown when aspect === "story"
const STORY_PRESETS = [
  { id: "none",          label: "None",          preview: "○" },
  { id: "kindness_wall", label: "Kindness Wall", preview: "◈" },
  { id: "mustard_frame", label: "Frame",         preview: "▢" },
];

// Draws the mustard "Kindness Wall" overlay onto a story canvas.
function drawKindnessWall(ctx, W, H) {
  const bandH = 420;
  const bandY = H - bandH;
  ctx.fillStyle = "#E8B33A";
  ctx.fillRect(0, bandY, W, bandH);
  ctx.fillStyle = "#E0623B";
  ctx.fillRect(0, bandY, W, 12);
  ctx.font = "700 30px Arial, sans-serif";
  ctx.fillStyle = "#5B3A1E";
  ctx.textBaseline = "top";
  ctx.fillText("SCHOOL FOR COMMUNITY LEARNING", 60, bandY + 60);
  ctx.font = "900 108px Georgia, serif";
  ctx.fillStyle = "#2B2B2B";
  ctx.fillText("Kindness Wall", 60, bandY + 120);
  ctx.font = "italic 40px Georgia, serif";
  ctx.fillStyle = "rgba(43,43,43,0.75)";
  ctx.fillText("A small act. A big ripple.", 60, bandY + 260);
  const pillW = 220, pillH = 60, pillX = 40, pillY = 40;
  ctx.fillStyle = "#E0623B";
  const r = 30;
  ctx.beginPath();
  ctx.moveTo(pillX + r, pillY);
  ctx.arcTo(pillX + pillW, pillY, pillX + pillW, pillY + pillH, r);
  ctx.arcTo(pillX + pillW, pillY + pillH, pillX, pillY + pillH, r);
  ctx.arcTo(pillX, pillY + pillH, pillX, pillY, r);
  ctx.arcTo(pillX, pillY, pillX + pillW, pillY, r);
  ctx.closePath();
  ctx.fill();
  ctx.font = "700 26px Arial, sans-serif";
  ctx.fillStyle = "#FFFFFF";
  ctx.textBaseline = "middle";
  ctx.fillText("SCL · KINDNESS", pillX + 22, pillY + pillH / 2 + 2);
}

function drawMustardFrame(ctx, W, H) {
  const t = 24;
  ctx.strokeStyle = "#E8B33A";
  ctx.lineWidth = t;
  ctx.strokeRect(t / 2, t / 2, W - t, H - t);
  ctx.strokeStyle = "#E0623B";
  ctx.lineWidth = 4;
  const inset = t + 8;
  ctx.strokeRect(inset, inset, W - inset * 2, H - inset * 2);
}

export default function InstagramCropper({ file, onCancel, onConfirm }) {
  const [aspectId, setAspectId] = useState("square");
  const [presetId, setPresetId] = useState("none");
  const A = ASPECTS[aspectId];
  const isStory = aspectId === "story";
  const [imgUrl, setImgUrl] = useState("");
  const [img, setImg] = useState(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [minScale, setMinScale] = useState(1);
  const drag = useRef({ active: false, sx: 0, sy: 0, ox: 0, oy: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImgUrl(url);
    const i = new Image();
    i.onload = () => setImg(i);
    i.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => { if (!isStory && presetId !== "none") setPresetId("none"); }, [isStory, presetId]);

  useEffect(() => {
    if (!img) return;
    const s = Math.max(A.viewW / img.width, A.viewH / img.height);
    setMinScale(s);
    setScale(s);
    setOffset({ x: (A.viewW - img.width * s) / 2, y: (A.viewH - img.height * s) / 2 });
  }, [img, aspectId]); // eslint-disable-line

  const clamp = useCallback((o, s) => {
    if (!img) return o;
    const w = img.width * s;
    const h = img.height * s;
    const minX = Math.min(0, A.viewW - w);
    const minY = Math.min(0, A.viewH - h);
    return {
      x: Math.max(minX, Math.min(0, o.x)),
      y: Math.max(minY, Math.min(0, o.y)),
    };
  }, [img, A.viewW, A.viewH]);

  useEffect(() => { setOffset((o) => clamp(o, scale)); }, [scale, clamp]);

  const onPointerDown = (e) => {
    e.preventDefault();
    const p = e.touches ? e.touches[0] : e;
    drag.current = { active: true, sx: p.clientX, sy: p.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    const p = e.touches ? e.touches[0] : e;
    const dx = p.clientX - drag.current.sx;
    const dy = p.clientY - drag.current.sy;
    setOffset(clamp({ x: drag.current.ox + dx, y: drag.current.oy + dy }, scale));
  };
  const onPointerUp = () => { drag.current.active = false; };

  const buildBlob = () => new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = A.outW; canvas.height = A.outH;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#EFE6DD";
    ctx.fillRect(0, 0, A.outW, A.outH);
    if (img) {
      const rx = A.outW / A.viewW;
      const ry = A.outH / A.viewH;
      ctx.drawImage(
        img,
        offset.x * rx,
        offset.y * ry,
        img.width * scale * rx,
        img.height * scale * ry,
      );
    }
    if (isStory && presetId === "kindness_wall") drawKindnessWall(ctx, A.outW, A.outH);
    if (isStory && presetId === "mustard_frame") drawMustardFrame(ctx, A.outW, A.outH);

    if (!(isStory && presetId === "kindness_wall")) {
      const scaleUp = A.outH > A.outW ? 1.2 : 1;
      const pad = 32 * scaleUp;
      const fontSize = 34 * scaleUp;
      ctx.font = `700 ${fontSize}px Georgia, serif`;
      const text = WATERMARK;
      const metrics = ctx.measureText(text);
      const boxW = metrics.width + 40 * scaleUp;
      const boxH = 58 * scaleUp;
      const bx = A.outW - boxW - pad;
      const by = A.outH - boxH - pad;
      ctx.fillStyle = "rgba(224, 98, 59, 0.92)";
      const r = 28 * scaleUp;
      ctx.beginPath();
      ctx.moveTo(bx + r, by);
      ctx.arcTo(bx + boxW, by, bx + boxW, by + boxH, r);
      ctx.arcTo(bx + boxW, by + boxH, bx, by + boxH, r);
      ctx.arcTo(bx, by + boxH, bx, by, r);
      ctx.arcTo(bx, by, bx + boxW, by, r);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.textBaseline = "middle";
      ctx.fillText(text, bx + 20 * scaleUp, by + boxH / 2 + 2);
    }
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92);
  });

  const confirm = async () => {
    setSaving(true);
    try {
      const blob = await buildBlob();
      const suffix = aspectId === "story"
        ? (presetId === "kindness_wall" ? "-kindness-wall" : (presetId === "mustard_frame" ? "-frame" : "-story"))
        : "-scl";
      const cropped = new File([blob], (file?.name || "photo").replace(/\.\w+$/, "") + `${suffix}.jpg`, { type: "image/jpeg" });
      onConfirm(cropped, { preset: aspectId === "story" ? presetId : "", aspect: aspectId });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-scl-charcoal/85 flex items-center justify-center p-4 overflow-y-auto" data-testid="cropper-modal" role="dialog" aria-modal="true">
      <div className="bg-white rounded-2xl w-full max-w-md p-5 shadow-warm my-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <CropIcon className="w-4 h-4 text-scl-coral" />
            <div className="font-serif font-bold text-scl-charcoal">Crop for social</div>
          </div>
          <button onClick={onCancel} data-testid="cropper-close" className="w-8 h-8 rounded-full hover:bg-scl-cream flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex gap-2 mb-3" data-testid="cropper-aspect-row">
          {Object.values(ASPECTS).map((asp) => {
            const Icon = asp.icon;
            const active = aspectId === asp.id;
            return (
              <button
                key={asp.id}
                type="button"
                onClick={() => setAspectId(asp.id)}
                data-testid={`cropper-aspect-${asp.id}`}
                className={`flex-1 min-h-[44px] rounded-full px-3 text-xs font-semibold border transition-all inline-flex items-center justify-center gap-1.5 ${active ? "bg-scl-coral text-white border-scl-coral shadow-warm-sm" : "bg-white text-scl-charcoal border-scl-border hover:bg-scl-cream"}`}
              >
                <Icon className="w-3.5 h-3.5" /> {asp.label}
              </button>
            );
          })}
        </div>

        {isStory && (
          <div className="mb-3" data-testid="cropper-preset-row">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-scl-charcoal/60 font-semibold mb-1.5">
              <Sparkles className="w-3 h-3 text-scl-mustard" /> Story preset
            </div>
            <div className="flex gap-2 flex-wrap">
              {STORY_PRESETS.map((p) => {
                const active = presetId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPresetId(p.id)}
                    data-testid={`cropper-preset-${p.id}`}
                    className={`min-h-[36px] rounded-full px-3 text-[11px] font-semibold border transition-all inline-flex items-center gap-1.5 ${active ? "bg-scl-mustard text-scl-charcoal border-scl-mustard shadow-warm-sm" : "bg-white text-scl-charcoal border-scl-border hover:bg-scl-cream"}`}
                  >
                    <span aria-hidden>{p.preview}</span> {p.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <p className="text-xs text-scl-charcoal/60 mb-3">Drag to reposition, use the slider to zoom.{isStory && presetId === "kindness_wall" ? " The Kindness Wall band fills the bottom third." : " A subtle SCL watermark is added at the bottom-right."}</p>

        <div
          className="relative bg-scl-cream rounded-xl overflow-hidden mx-auto touch-none select-none"
          style={{ width: A.viewW, height: A.viewH }}
          onMouseDown={onPointerDown} onMouseMove={onPointerMove} onMouseUp={onPointerUp} onMouseLeave={onPointerUp}
          onTouchStart={onPointerDown} onTouchMove={onPointerMove} onTouchEnd={onPointerUp}
          data-testid="cropper-viewport"
        >
          {imgUrl && img && (
            <img
              src={imgUrl}
              alt=""
              draggable={false}
              className="absolute pointer-events-none"
              style={{ left: offset.x, top: offset.y, width: img.width * scale, height: img.height * scale, maxWidth: "none" }}
            />
          )}
          {isStory && presetId === "kindness_wall" && (
            <div className="absolute inset-x-0 bottom-0 bg-scl-mustard border-t-[3px] border-scl-coral px-3 pt-2 pb-3" style={{ height: (A.viewH * 420) / 1920 + 20 }} data-testid="preset-preview-kindness_wall">
              <div className="text-[7px] tracking-widest text-scl-charcoal/80 font-bold">SCHOOL FOR COMMUNITY LEARNING</div>
              <div className="font-serif font-black text-scl-charcoal leading-tight text-lg">Kindness Wall</div>
              <div className="italic text-[9px] text-scl-charcoal/70">A small act. A big ripple.</div>
              <div className="absolute top-1.5 left-1.5 bg-scl-coral text-white text-[7px] font-bold px-1.5 py-0.5 rounded-full">SCL · KINDNESS</div>
            </div>
          )}
          {isStory && presetId === "mustard_frame" && (
            <div className="absolute inset-1 border-4 border-scl-mustard rounded-sm pointer-events-none" data-testid="preset-preview-mustard_frame">
              <div className="absolute inset-1 border border-scl-coral rounded-sm" />
            </div>
          )}
          {!(isStory && presetId === "kindness_wall") && (
            <div className="absolute bottom-2 right-2 bg-scl-coral text-white text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded-full opacity-90">{WATERMARK}</div>
          )}
        </div>

        <div className="mt-3">
          <label className="text-[10px] uppercase tracking-widest text-scl-charcoal/60 font-semibold flex items-center gap-1"><RotateCw className="w-3 h-3" /> Zoom</label>
          <input
            type="range"
            min={minScale}
            max={minScale * 4}
            step={0.01}
            value={scale}
            onChange={(e) => setScale(parseFloat(e.target.value))}
            data-testid="cropper-zoom"
            className="w-full accent-scl-coral"
          />
        </div>

        <div className="mt-4 flex gap-2 justify-end">
          <button onClick={onCancel} data-testid="cropper-cancel" className="h-10 px-4 rounded-full border border-scl-border text-sm font-medium hover:bg-scl-cream">Cancel</button>
          <button onClick={confirm} disabled={!img || saving} data-testid="cropper-confirm" className="h-10 px-5 rounded-full bg-scl-coral text-white font-semibold text-sm flex items-center gap-2 hover:brightness-105 disabled:opacity-60">
            <Check className="w-4 h-4" /> {saving ? "Cropping…" : "Use this crop"}
          </button>
        </div>
      </div>
    </div>
  );
}
